import 'react-perfect-scrollbar/dist/css/styles.css';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import Message from 'components/Message';
import api from 'services/api';
import { ModalContext } from 'context/ModalContext';
import { MODAL_ANIMATIONS, MODAL_TYPES } from 'components/Modal/constants';
import { togglePulsing } from 'store/buttonSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'components/Menu';
import { SecondaryMenuItem } from 'components/Menu/SecondaryMenuItem';
import { ChevronDownIcon } from 'components/icons/ChevronDownIcon';
import { isMobile } from 'react-device-detect';

import AddForm from './_components/AddForm';
import { handleAddNote } from 'store/channelNoteSlice';

import { selectNoteByChannelAndVideo } from 'store/channelNoteSlice';
import NoteItem from './_components/NoteItem';
import { deleteSpecificNote } from 'api/notesAPI';

interface INoteView {
  channelId: string;
  navigateVideoPage: any;
  jumpToVideo: any;
  currentVideoShot: any;
  selectedVideo: any;
  setCurrentVideoShot: any;
  videoId: string;
  videoHashId: string;
  secondaryViewData?: any;
  selected: number;
  videoShots: any;
}

function NoteView({
  channelId,
  navigateVideoPage,
  jumpToVideo,
  currentVideoShot,
  selectedVideo,
  setCurrentVideoShot,
  videoId,
  videoHashId,
  secondaryViewData,
  selected,
  videoShots
}: INoteView) {
  // const notesShots = useSelector((state: RootState) => state.notes.noteList);
  // const currentTime = useSelector((state: RootState) => state.videoSeektime.currentTime);
  const videoNotes = useSelector((state: RootState) =>
    selectNoteByChannelAndVideo(state, channelId, secondaryViewData.hashId)
  );

  const [selectedChannel, setSelectedChannel] = useState(null as any);
  const channelList = useSelector((state: RootState) => state.channels.channelList);
  const { showModal } = useContext(ModalContext);
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const videoMatch = location.pathname.match(/\/video\/([^/]+)/);
  const activeVideoPageId = videoMatch ? videoMatch[1] : undefined;
  const [getactiveVideoShot, setGetactiveVideoShot] = useState('');
  const [editingId, setEditingId] = useState<null | string>(null);
  const userData = useSelector((state: RootState) => state.user.user);
  const [hoveredId, setHoveredId] = useState(null as any);

  const videoShotId = useMemo(() => {
    if (!videoShots?.length) return;
    const idx = videoShots[currentVideoShot]?.shot_hash_id;
    return idx;
  }, [secondaryViewData.hashId, currentVideoShot]);

  const handleDelete = async (hashId: string) => {
    const confirmDelete = async () => {
      await deleteSpecificNote(hashId);
    };

    showModal(MODAL_TYPES.remove_summary, {
      animation: MODAL_ANIMATIONS.fade,
      modalMessage: {
        title: 'Deleting Note',
        text: 'You are about to to delete a note.  Click "Confirm to delete" button.'
      },
      onConfirm: confirmDelete
    });
  };

  const handleEdit = (hashId: string) => {
    if (editingId !== null && editingId !== hashId) {
      showModal(MODAL_TYPES.edit_summary, {
        animation: MODAL_ANIMATIONS.fade,
        channelId,
        setEditingId,
        editingId: hashId,
        itemType: 'note'
      });
    } else {
      setEditingId(hashId);
    }
  };

  useEffect(() => {
    const fetchData = () => {
      const apiUrl =
        channelId !== 'home'
          ? `/api/videos/${secondaryViewData.hashId}/${channelId}/notes/`
          : `/api/videos/${secondaryViewData.hashId}/notes/`;
      api.get(apiUrl).then((res) => {
        dispatch(
          handleAddNote({
            channelHashId: channelId,
            videoHashId: secondaryViewData.hashId,
            data: res.data
          })
        );
      });
    };

    if (!videoNotes || videoNotes.length === 0) {
      fetchData();
    }
  }, [channelId, secondaryViewData.hashId, videoShotId]);

  useEffect(() => {
    setGetactiveVideoShot(videoShotId);
  }, [videoShotId]);

  useEffect(() => {
    if (channelId) {
      setSelectedChannel(channelList.find((channel) => channel.hash_id === channelId));
    }
  }, [channelList, channelId]);

  const navigateChannel = (channelHashId?: any) => {
    const videoElement = document.getElementById((channelHashId ? channelHashId : 'home') + '-' + selectedVideo.id);
    if (videoElement) {
      videoElement.scrollIntoView();
      dispatch(togglePulsing());
      setTimeout(() => {
        dispatch(togglePulsing());
      }, 3000);
    } else {
      // dispatch(
      //   setRecentVideoPrevNextGroup({
      //     key: channelHashId ? channelHashId : 'home'
      //   })
      // );
      navigateTo(channelHashId ? `/user/channel/${channelHashId}` : '/home', {
        state: {
          fromNotification: true,
          videoElement: (channelHashId ? channelHashId : 'home') + '-' + selectedVideo.id,
          dateAdded: selectedVideo.dateAdded,
          channelHashId: channelHashId ? channelHashId : 'home'
        }
      });
    }
  };

  const createNewNote = (content: string, time: number) => {
    return api.post('/api/videos/note/', {
      channel_id: selectedChannel?.hash_id,
      shot_id: getactiveVideoShot,
      time: time,
      content: content
    });
  };

  if (!videoNotes) {
    return null;
  }

  return (
    <div className={`absolute w-full flex flex-col flex-1 top-0 right-0 left-0 bottom-0 overflow-y-hidden`}>
      <PerfectScrollbar
        style={{
          maxHeight: isMobile ? 'calc(100% - 100px)' : activeVideoPageId ? 'calc(100% - 140px)' : 'calc(100% - 20px)'
        }}
        options={{ suppressScrollY: false, suppressScrollX: true }}
      >
        {videoNotes.map((note: any, index: number) => {
          return (
            <div className="relative w-full" key={note.videoshot_hash_id}>
              {videoNotes.length > 1 && (
                <>
                  <div
                    className="bg-white sticky top-2 z-40 w-fit mx-auto rounded-lg mt-[6px] max-w-[calc(100%-100px)] mb-2"
                    id={note.videoshot_hash_id}
                  >
                    <Menu
                      menus={videoNotes.map((menuNote: any, idx: number) => ({
                        id: menuNote.videoshot_hash_id,
                        name: menuNote.name,
                        selected: note.videoshot_hash_id === menuNote.videoshot_hash_id,
                        onClick: () => {
                          setCurrentVideoShot(idx);
                          jumpToVideo(`note-${videoNotes[idx].videoshot_hash_id}`);
                        }
                      }))}
                      align="center"
                      position="absolute"
                      top="mt-8"
                      width={265}
                      MenuItem={SecondaryMenuItem}
                      selector={note.videoshot_hash_id}
                      additionalClasses="flex justify-center"
                    >
                      <div
                        className="pl-4 pr-2 h-8 leading-8 rounded-lg text-[13px] font-lato font-bold cursor-pointer w-full flex relative items-center mx-auto z-10 bg-black bg-opacity-5 hover:bg-opacity-10"
                        onClick={() => setCurrentVideoShot(index)}
                      >
                        <span className="truncate w-[calc(100%-24px)]">{note.name}</span>
                        <ChevronDownIcon />
                      </div>
                    </Menu>
                  </div>
                  <div className="border-t absolute w-full top-[18px] border-iconColor border-opacity-20 border-b-0"></div>
                </>
              )}
              {note?.notes?.map((item: any, idx: number) => (
                <NoteItem
                  key={item?.hash_id}
                  videoHashId={videoHashId}
                  hashId={item?.hash_id}
                  displayAvatar={idx === 0 || (idx && note.notes[idx - 1].note_user.user_id !== item.note_user.user_id)}
                  user={{ avatar: item?.note_user?.avatar, display_name: item?.note_user?.display_name }}
                  content={item.content}
                  navigateLink={navigateVideoPage}
                  time={item.time}
                  userData={userData}
                  handleEdit={() => {
                    handleEdit(item.hash_id);
                  }}
                  isEditing={editingId === item.hash_id}
                  handleCancelEdit={() => {
                    setEditingId(null);
                  }}
                  onDelete={() => {
                    handleDelete(item.hash_id);
                  }}
                  item={item}
                  hoveredId={hoveredId}
                  handleHoveredId={() => {
                    setHoveredId(item.hash_id);
                  }}
                />
              ))}
            </div>
          );
        })}
      </PerfectScrollbar>
      <div className="text-[13px] text-[#111]">
        {selectedChannel ? (
          <>
            {activeVideoPageId && (
              <AddForm
                channelId={channelId}
                onSubmit={createNewNote}
                textinputPlaceholder={`Write a note for #${selectedChannel?.name}`}
                selected={selected}
              ></AddForm>
            )}
          </>
        ) : (
          <div className="mb-4 pb-1 border-t border-[#e5e6e7] rounded-md w-full">
            {selectedVideo?.channels?.length === 0 ? (
              <p className="px-3 py-[6px]">
                <span
                  className="text-[#1664A3] cursor-pointer"
                  onClick={() =>
                    showModal(MODAL_TYPES.share_video, {
                      animation: MODAL_ANIMATIONS.unfold,
                      videoId: videoId,
                      videoHashId: videoHashId,
                      videoName: name
                    })
                  }
                >
                  Share this video{' '}
                </span>
                to a channel to add notes
              </p>
            ) : (
              <p className="px-3 py-[6px]">
                Select a channel to add notes: <br />
                {[...(selectedVideo?.channels || [])]
                  .sort((channelA: { is_archived: any }, ChannelB: { is_archived: any }) => {
                    if (channelA.is_archived === ChannelB.is_archived) {
                      return 0;
                    }
                    if (channelA.is_archived) {
                      return 1;
                    } else {
                      return -1;
                    }
                  })
                  .map((item: any, idx: number) => (
                    <span
                      className={` ${
                        item.is_archived ? '' : 'text-[#1664A3] font-bold hover:underline cursor-pointer '
                      }`}
                      key={item.channel.name}
                      onClick={() => {
                        if (item.is_archived) {
                          return;
                        }
                        setSelectedChannel(channelList.find((channel) => channel.hash_id === item.channel.hash_id));
                        navigateChannel(item.channel.hash_id);
                      }}
                    >
                      {idx !== 0 && ', '}
                      {`#${item.channel.name}${item.is_archived ? ' (archived)' : ''}`}
                    </span>
                  ))}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(NoteView);
